import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Icons from 'lucide-react'
import { Loader2, Play, ShieldAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { UnitPicker } from '@/features/incidents/wizard/UnitPicker'
import { EntityPicker } from '@/shared/pickers/EntityPicker'
import { listCouncils, listPlaybooks, startRun, type Council, type Playbook } from './api'
import { CATEGORY_LABEL, CATEGORY_ORDER, STAGE_KIND_LABEL } from './vocab'

/**
 * The playbook gallery: pick a question, pick a subject, run a council.
 *
 * ## The card shows what it will ACTUALLY do
 *
 * Every card names the council it will run and — because the server dry-routes
 * every stage — which model each stage would get right now. That turns "run the
 * seat outlook" from a leap of faith into a decision somebody can make: it says
 * up front that the red team has no second vendor, or that no configured model
 * holds `JSON_SCHEMA`, before any money is spent finding out.
 *
 * ## Sensitive playbooks are shown to people who cannot run them
 *
 * They are listed, marked, and the button says which authority is missing. Hiding
 * them would leave somebody unable to ask for the access they need because they
 * do not know the capability exists.
 */
export function ConsolePage() {
  const navigate = useNavigate()
  const [playbooks, setPlaybooks] = useState<Playbook[] | null>(null)
  const [councils, setCouncils] = useState<Council[]>([])
  const [error, setError] = useState<string | null>(null)
  const [chosen, setChosen] = useState<Playbook | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      const [p, c] = await Promise.all([listPlaybooks(), listCouncils()])
      setPlaybooks(p)
      setCouncils(c)
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const byCategory = useMemo(() => {
    const map = new Map<string, Playbook[]>()
    ;(playbooks ?? []).forEach((p) => {
      const list = map.get(p.category) ?? []
      list.push(p)
      map.set(p.category, list)
    })
    return map
  }, [playbooks])

  const councilByCode = useMemo(() => {
    const map = new Map<string, Council>()
    councils.forEach((c) => map.set(c.code, c))
    return map
  }, [councils])

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-erp-lg font-semibold">Console</h1>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          Each of these is a governed prompt with a brief recipe, a council and an authority.
          Nothing here is a text box that reaches a model directly.
        </p>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {playbooks === null && !error && <LoadingBlock />}

      {playbooks && CATEGORY_ORDER.filter((c) => byCategory.has(c)).map((category) => (
        <section key={category}>
          <h2 className="text-erp-sm font-medium mb-1.5">
            {CATEGORY_LABEL[category] ?? category}
          </h2>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {(byCategory.get(category) ?? []).map((playbook) => (
              <PlaybookCard key={playbook.code} playbook={playbook}
                council={councilByCode.get(playbook.councilCode)}
                onRun={() => setChosen(playbook)} />
            ))}
          </div>
        </section>
      ))}

      {chosen && (
        <RunDialog playbook={chosen} council={councilByCode.get(chosen.councilCode)}
          onClose={() => setChosen(null)}
          onStarted={(runId) => navigate(`/advisory/runs/${runId}`)} />
      )}
    </div>
  )
}

function PlaybookCard({ playbook, council, onRun }: {
  playbook: Playbook
  council?: Council
  onRun: () => void
}) {
  const may = useCan(playbook.requiredAuthority)
  const Icon = (playbook.icon && (Icons as unknown as Record<string, typeof Play>)[playbook.icon])
    || Icons.Sparkles

  const unroutable = council?.stages.filter((s) => !s.resolvedModel) ?? []

  return (
    <div className={cn('rounded-md border border-border px-3 py-2.5 flex flex-col gap-1.5',
      playbook.sensitivity === 'SENSITIVE' && 'border-l-[3px] border-l-amber-500')}>
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-chocolate" />
        <div className="min-w-0 flex-1">
          <div className="text-erp-sm font-medium">{playbook.title}</div>
          <p className="text-erp-xs text-muted-foreground mt-0.5">{playbook.summary}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {council?.stages.map((stage) => (
          <span key={stage.key} title={stage.routingNote ?? undefined}
            className={cn('rounded border px-1.5 py-px text-erp-xs',
              stage.resolvedModel
                ? 'border-border text-muted-foreground'
                : 'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-500/10')}>
            {STAGE_KIND_LABEL[stage.kind] ?? stage.kind}
            {stage.resolvedVendor && ` · ${stage.resolvedVendor}`}
          </span>
        ))}
      </div>

      {unroutable.length > 0 && (
        <p className="text-erp-xs text-amber-700 dark:text-amber-400">
          {unroutable.map((s) => s.routingNote).filter(Boolean)[0]}
        </p>
      )}

      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        <span className="text-erp-xs text-muted-foreground">
          {playbook.contributors.length} data sections · {playbook.councilCode.toLowerCase()}
        </span>
        {may ? (
          <button type="button" onClick={onRun}
            className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
            <Play className="h-3.5 w-3.5" /> Run
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 text-erp-xs text-muted-foreground">
            <ShieldAlert className="h-3.5 w-3.5" />
            needs {playbook.requiredAuthority}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Choosing the subject.
 *
 * The scope kind comes from the playbook, not from a dropdown of eleven options —
 * a seat outlook is about a unit and a contrast brief is about a candidate, and
 * asking the reader to work that out is asking them to do the playbook's job.
 */
function RunDialog({ playbook, council, onClose, onStarted }: {
  playbook: Playbook
  council?: Council
  onClose: () => void
  onStarted: (runId: string) => void
}) {
  const [scopeRef, setScopeRef] = useState<string | null>(null)
  const [question, setQuestion] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const kind = playbook.defaultScopeKind
  const needsRef = kind !== 'NATIONAL' && kind !== 'SYSTEM'

  const start = async () => {
    setBusy(true)
    setError(null)
    try {
      const run = await startRun({
        playbookCode: playbook.code,
        scopeKind: kind,
        scopeRef,
        question: question.trim() || undefined,
      })
      onStarted(run.id)
    } catch (err) {
      setError(explain(err))
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-md border border-border bg-background p-4 space-y-3">
        <div>
          <h2 className="text-erp-sm font-semibold">{playbook.title}</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">{playbook.summary}</p>
        </div>

        {playbook.sensitivity === 'SENSITIVE' && (
          <Note tone="info">
            This playbook is marked <span className="font-medium">sensitive</span>: its brief is
            either this deployment&rsquo;s own security posture or a named person&rsquo;s record,
            and running it sends that to the configured providers. Every read of the brief
            afterwards is audited.
          </Note>
        )}

        {needsRef && (
          <div>
            <label className="text-erp-xs text-muted-foreground">
              {kind === 'UNIT' ? 'Which unit?' : `Which ${kind.toLowerCase()}?`}
            </label>
            {kind === 'UNIT' ? (
              <UnitPicker value={scopeRef} onPick={(u) => setScopeRef(u?.id ?? null)} />
            ) : kind === 'CANDIDATE' || kind === 'PARTY' || kind === 'COALITION' ? (
              <EntityPicker
                source={kind.toLowerCase() as 'candidate' | 'party' | 'coalition'}
                value={scopeRef}
                onChange={setScopeRef}
                placeholder={`Search ${kind.toLowerCase()}s…`} />
            ) : (
              <input className="erp-input w-full text-erp-sm" value={scopeRef ?? ''}
                onChange={(e) => setScopeRef(e.target.value || null)}
                placeholder={`${kind} id`} />
            )}
          </div>
        )}

        <div>
          <label className="text-erp-xs text-muted-foreground">
            Anything specific to steer it? (optional)
          </label>
          <textarea rows={2} value={question} onChange={(e) => setQuestion(e.target.value)}
            className="erp-input w-full text-erp-sm"
            placeholder="e.g. focus on the wards where transport is the constraint" />
        </div>

        {council && (
          <div className="rounded-md border border-border bg-muted/20 px-2.5 py-2">
            <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground">
              What will run
            </div>
            <ul className="mt-1 space-y-0.5">
              {council.stages.map((stage) => (
                <li key={stage.key} className="text-erp-xs">
                  <span className="text-muted-foreground">
                    {STAGE_KIND_LABEL[stage.kind] ?? stage.kind}:
                  </span>{' '}
                  {stage.resolvedModel
                    ? `${stage.resolvedVendor} ${stage.resolvedModel}`
                    : <span className="text-amber-700 dark:text-amber-400">
                        {stage.routingNote ?? 'no model available'}
                      </span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <ErrorStrip message={error} className="rounded-md border" />

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="erp-btn h-9 px-3 text-erp-sm">
            Cancel
          </button>
          <button type="button" disabled={busy || (needsRef && !scopeRef)}
            onClick={() => void start()}
            className="erp-btn erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center
              gap-2">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Play className="h-3.5 w-3.5" />}
            Run the council
          </button>
        </div>
      </div>
    </div>
  )
}
